import { Action } from 'redux';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { concat, EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { changeLocation } from '../../actions/common';
import { closeEditPageItemNameEditor, showEditPageItemNameEditorErrors } from '../../actions/editPageItemNameEditor';
import { RoutesPath } from '../../routes/index';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { EpicDeps } from '../../service/StoreFactory';
import { PageStatus } from '../../typings/common';
import { State } from '../../typings/state';
import {
    getApiVideoDataForVideoSave,
    getImageFromVideoResources,
    getVideoResourcesFromApiResponse,
    getVideoResourcesFromTemplate,
    mergeTemplateWithVideoResources
} from '../api/mappers';
import { VideoCreatorApiMethodExecutor } from '../api/VideoCreatorApi';
import { VideoCreatorHelpers } from '../VideoCreatorHelper/VideoCreatorHelper';
import { initImageUploader } from '../VideoCreatorImageUploader/actions';

import {
    CHANGE_VIDEO_CREATOR_IMAGE,
    ChangeVideoCreatorImageAction,
    changeVideoCreatorPageStatus,
    changeVideoCreatorVideoName,
    GET_VIDEO_CREATOR_DATA_FOR_EDIT,
    GetVideoCreatorDataForEditAction,
    LEAVE_VIDEO_CREATOR_EDIT,
    SAVE_VIDEO_FROM_VIDEO_CREATOR,
    SAVE_VIDEO_NAME_EDITOR_CHANGES,
    SaveVideoFromVideoCreatorAction,
    SaveVideoNameEditorChangesAction,
    updateVideoCreatorDataForEdit,
    updateVideoCreatorParam
} from './actions';
import { getVideoCreatorSaveDataValidationResult, getVideoNameEditorValidationResult } from './helpers/helpers';

const getVideoCreatorDataForEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
): Observable<Action> => {
    return action$.pipe(
        ofType(GET_VIDEO_CREATOR_DATA_FOR_EDIT),
        switchMap(
            (action: GetVideoCreatorDataForEditAction): Observable<Action> => {
                const emptyResponse = Promise.resolve(undefined);
                const api = new VideoCreatorApiMethodExecutor();

                const {
                    router: { query = {} },
                    commonData: {
                        client: { ClientID }
                    }
                } = store$.value;

                return forkJoin(
                    ClientID && query.id ? api.getUserVideoData(ClientID, query.id) : emptyResponse,
                    VideoCreatorHelpers.getTemplateById(query.templateId)
                ).pipe(
                    mergeMap(
                        ([response, template]): Observable<Action> => {
                            let videoResources,
                                params = {};

                            if (response) {
                                videoResources = getVideoResourcesFromApiResponse(response);
                                params = {
                                    name: response.name,
                                    id: response.id
                                };
                            } else {
                                videoResources = getVideoResourcesFromTemplate(template);
                            }
                            const mergedTemplate = mergeTemplateWithVideoResources(template, videoResources, params);
                            const image = getImageFromVideoResources(videoResources);

                            return from([
                                initImageUploader(image.url, image.id),
                                updateVideoCreatorDataForEdit(mergedTemplate, videoResources)
                            ]);
                        }
                    ),
                    catchError(() => EMPTY)
                );
            }
        )
    );
};

const saveVideoFromVideoCreatorEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
): Observable<Action> => {
    return action$.pipe(
        ofType(SAVE_VIDEO_FROM_VIDEO_CREATOR),
        switchMap(
            (action: SaveVideoFromVideoCreatorAction): Observable<Action> => {
                const state = store$.value;
                const emptyResponse = Promise.resolve(undefined);
                const api = new VideoCreatorApiMethodExecutor();

                const {
                    commonData: {
                        login,
                        client: { ClientID }
                    },
                    videoCreatorEdit
                } = state;
                const videoCreatorApiVideoData = getApiVideoDataForVideoSave(state);
                const validation = getVideoCreatorSaveDataValidationResult(videoCreatorEdit.id, state);

                if (!validation.valid) {
                    return concat([...validation.showErrorsActions, changeVideoCreatorPageStatus(PageStatus.LOADED)]);
                }

                return from(ClientID ? api.postUserVideoData(ClientID, videoCreatorApiVideoData) : emptyResponse).pipe(
                    mergeMap(
                        (response): Observable<Action> => {
                            return of(changeLocation(RoutesPath.videoCreatorMyVideo, { ulogin: login }, false));
                        }
                    ),
                    catchError(() => EMPTY)
                );
            }
        )
    );
};

const updateVideoCreatorVideoNameEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_VIDEO_NAME_EDITOR_CHANGES),
        switchMap((action: SaveVideoNameEditorChangesAction): Action[] => {
            const state = store$.value;
            const { id } = state.videoCreatorEdit;
            const { editPageItemNameEditor } = state;
            const editorData = selectEditPageItemNameEditorValue(editPageItemNameEditor, id);

            const validationErrors = getVideoNameEditorValidationResult(id, state);

            if (validationErrors.length === 0) {
                return [changeVideoCreatorVideoName(editorData.value), closeEditPageItemNameEditor(id)];
            }

            return [showEditPageItemNameEditorErrors(id, validationErrors)];
        })
    );
};

const leaveVideoCreatorEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
): Observable<Action> => {
    return action$.pipe(
        ofType(LEAVE_VIDEO_CREATOR_EDIT),
        switchMap(
            (): Observable<Action> => {
                const {
                    commonData: { login },
                    router: { previous }
                } = store$.value;
                let path = RoutesPath.videoCreatorTemplatesList;

                if (previous) {
                    switch (previous.route) {
                        case RoutesPath.videoCreatorTemplatesList:
                            path = previous.route;
                            break;
                        case RoutesPath.videoCreatorMyVideo:
                            path = previous.route;
                            break;
                        default:
                            path = RoutesPath.videoCreatorTemplatesList;
                    }
                }

                return of(changeLocation(path, { ulogin: login }, false));
            }
        )
    );
};

const changeVideoCreatorImageEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(CHANGE_VIDEO_CREATOR_IMAGE),
        switchMap(
            (action: ChangeVideoCreatorImageAction): Observable<Action> => {
                const state = store$.value;
                const { paramsId, backgroundImage } = action;
                const draftVideoResource = Object.assign({}, state.videoCreatorEdit.videoResources[paramsId], {
                    backgroundImage
                });

                return of(updateVideoCreatorParam(paramsId, draftVideoResource));
            }
        )
    );
};

export const videoCreatorEditEpics = combineEpics(
    getVideoCreatorDataForEditEpic,
    saveVideoFromVideoCreatorEpic,
    updateVideoCreatorVideoNameEpic,
    leaveVideoCreatorEditEpic,
    changeVideoCreatorImageEpic
);
